// useEditorGuard — context-published surface for non-close guarded actions
// inside `EditorDrawer` / `EditorModal` / `EditorPage` subtrees.
//
// @aiPurpose Expose `confirmDestructive` and `bypassNext` callbacks from the
//   single `useUnsavedChangesGuard` instance owned by the surrounding editor
//   wrapper so consumers can guard tab switches, in-page destructive actions,
//   and synchronous post-save navigations WITHOUT calling
//   `useUnsavedChangesGuard` directly (#680 will forbid that import).
// @aiInvariant Throws a developer-visible error when used outside an
//   `EditorDrawer` / `EditorModal` / `EditorPage` subtree — there is no silent
//   fallback.
import { createContext, useContext } from 'react';

export interface EditorGuardValue {
  /**
   * Guard a non-close destructive action (e.g. tab switch, in-page delete).
   * Mirrors `useUnsavedChangesGuard().confirmDestructive`.
   */
  confirmDestructive: (action: () => void) => void;
  /**
   * One-shot bypass for the very next blocker dispatch. Mirrors
   * `useUnsavedChangesGuard().bypassNext`.
   */
  bypassNext: () => void;
}

export const EditorGuardContext = createContext<EditorGuardValue | null>(null);

export function useEditorGuard(): EditorGuardValue {
  const ctx = useContext(EditorGuardContext);
  if (!ctx) {
    throw new Error(
      'useEditorGuard must be used within an <EditorDrawer>, <EditorModal> or <EditorPage>',
    );
  }
  return ctx;
}
